//src/components/component/home/price-tracking-hero.tsx
'use client';
import React, { useState, useEffect, useRef, ReactNode } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { ArrowUpCircle, ArrowDownCircle, Loader2 } from 'lucide-react';
import GlowButton from './glow-button';
import HeroSearch from './hero-search';
import { SearchResult } from '../dashboard/watchlist/watchlist.types';
import { RolexImage } from '@/utils/RolexImage';
import GridBackground from './grid-background';
import { createDatafeedAdapter } from './datafeed-adapter';

interface PriceTrackingHeroProps {
  datafeed?: any;
  onTryItFree?: () => void;
}

interface GlowEffectContainerProps {
  children: ReactNode;
}

interface PriceDataPoint {
  date: string;
  price: number;
  time: number;
}

interface ApiResponse {
  t: number;
  p: number;
  time?: number;
  close?: number;
}

const GlowEffectContainer: React.FC<GlowEffectContainerProps> = ({
  children,
}) => {
  return (
    <div className="relative w-full">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 right-0 w-2/3 h-2/5 bg-blue-500/10 rounded-full blur-[100px] transform translate-x-1/4 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-2/3 h-2/5 bg-blue-500/10 rounded-full blur-[100px] transform -translate-x-1/4 translate-y-1/4" />
      </div>

      <div className="relative z-10">{children}</div>

      <div className="absolute -inset-10 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/10 via-transparent to-transparent opacity-50" />
        <div className="absolute -left-10 -right-10 bottom-0 h-[500px] bg-gradient-to-t from-blue-500/20 via-blue-500/5 to-transparent" />
      </div>
    </div>
  );
};

const applySmoothing = (
  data: PriceDataPoint[],
  period: number = 20
): PriceDataPoint[] => {
  return data.map((point, index) => {
    if (index < period) return point;

    const window = data.slice(Math.max(0, index - period + 1), index + 1);
    const weightedSum = window.reduce((sum, p, i) => {
      const weight = Math.exp(i / window.length);
      return sum + p.price * weight;
    }, 0);
    const weights = window.reduce(
      (sum, _, i) => sum + Math.exp(i / window.length),
      0
    );

    return {
      ...point,
      price: Number((weightedSum / weights).toFixed(2)),
    };
  });
};

const fetchPriceData = async (
  symbol: string = 'Rolex:16610'
): Promise<PriceDataPoint[]> => {
  try {
    // Add a cache check to prevent redundant API calls
    const cacheKey = `price-data-${symbol}`;
    const cachedData = sessionStorage.getItem(cacheKey);

    if (cachedData) {
      return JSON.parse(cachedData);
    }

    // Calculate 5 years ago timestamp for main chart
    const now = Date.now();
    const fiveYearsAgo = now - 5 * 365 * 24 * 60 * 60 * 1000;
    const fromTimestamp = Math.floor(fiveYearsAgo / 1000);
    const toTimestamp = Math.floor(now / 1000);

    // First try to get data range to check availability
    const rangeResponse = await fetch(
      `/api/trading-dashboard/getDataRange?symbol=${symbol}&source=forum`
    );
    const range = await rangeResponse.json();

    // Determine the earliest point we can fetch (either 5 years or earliest available)
    const earliestTimestamp = Math.max(
      fromTimestamp,
      range.firstDataPoint || fromTimestamp
    );

    const response = await fetch(
      `/api/trading-dashboard/getBars?symbol=${symbol}&from=${earliestTimestamp}&to=${range.lastDataPoint || toTimestamp}&simple=true&source=forum`
    );
    const data = await response.json();

    // Map the data to the expected format with proper validation
    const transformedData = data
      .filter(
        (item: ApiResponse) =>
          item &&
          typeof item.time !== 'undefined' &&
          (typeof item.close !== 'undefined' || typeof item.p !== 'undefined')
      )
      .map((item: ApiResponse) => ({
        date: new Date(item.time || item.t || 0).toLocaleDateString('en-US', {
          month: 'short',
          year: 'numeric',
        }),
        // Use close if available, otherwise use p
        price: Number((item.close || item.p || 0).toFixed(2)),
        time: item.time || item.t || 0,
      }))
      .sort((a: PriceDataPoint, b: PriceDataPoint) => a.time - b.time);

    const smoothedData = applySmoothing(transformedData, 20);

    // Cache the result to prevent unnecessary fetching
    sessionStorage.setItem(cacheKey, JSON.stringify(smoothedData));

    return smoothedData;
  } catch (error) {
    console.error('Error fetching price data:', error);
    return [];
  }
};

const PriceTrackingHero: React.FC<PriceTrackingHeroProps> = ({
  datafeed,
  onTryItFree,
}) => {
  const [priceData, setPriceData] = useState<PriceDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedSymbol, setSelectedSymbol] = useState('Rolex:16610');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [selectedWatchInfo, setSelectedWatchInfo] = useState<{
    brand: string;
    model: string;
    subModel?: string;
  }>({
    brand: 'Rolex',
    model: '16610',
    subModel: 'Submariner',
  });
  const [isMobile, setIsMobile] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);

  // Add a ref to track if search has been initialized
  const searchInitializedRef = useRef(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    // Check initially
    checkMobile();

    // Add resize listener with throttling to improve performance
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkMobile, 100);
    };

    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, []);

  const getModelNumber = (symbol: string): string => {
    if (symbol.includes(':')) {
      return symbol.split(':')[1];
    }
    return symbol;
  };
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await fetchPriceData(selectedSymbol);
        setPriceData(data);
        setHasLoadedInitialData(true);
      } catch (error) {
        console.error('Error loading price data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Only load data if it hasn't been loaded yet or if the symbol has changed
    if (!hasLoadedInitialData || selectedSymbol !== 'Rolex:16610') {
      loadData();
    }
  }, [selectedSymbol, hasLoadedInitialData]);

  const handleSymbolSelect = (result: SearchResult) => {
    // Only fetch new data if the symbol actually changed
    if (result.symbol !== selectedSymbol) {
      setSelectedSymbol(result.symbol);
      const [brand, model] = result.symbol.split(':');
      setSelectedWatchInfo({
        brand,
        model,
        subModel: result.subModel,
      });
      setSearchResults((prev) => {
        // Prevent duplicates in searchResults
        if (!prev.some((item) => item.symbol === result.symbol)) {
          return [...prev, result];
        }
        return prev;
      });
      setIsLoading(true); // Start loading when a symbol is selected
    }
  };

  // Create a memoized datafeed adapter that only initializes once
  const memoizedDatafeed = React.useMemo(() => {
    // Always create the datafeed adapter, but only once
    if (!searchInitializedRef.current) {
      searchInitializedRef.current = true;
      return createDatafeedAdapter();
    }
    return createDatafeedAdapter(); // Return a new instance if needed
  }, []);

  const currentPrice =
    !isLoading && priceData.length > 0
      ? Number((priceData[priceData.length - 1]?.price || 0).toFixed(2))
      : null;
  const previousPrice =
    !isLoading && priceData.length > 1
      ? Number((priceData[priceData.length - 2]?.price || 0).toFixed(2))
      : null;
  const priceChange =
    currentPrice && previousPrice
      ? Number((currentPrice - previousPrice).toFixed(2))
      : null;
  const percentChange =
    currentPrice && previousPrice && priceChange !== null
      ? Number(((priceChange / previousPrice) * 100).toFixed(1))
      : null;
  const isPositive = priceChange ? priceChange >= 0 : null;

  const getFormattedWatchName = () => {
    const { brand, model, subModel } = selectedWatchInfo;
    return subModel ? `${brand} ${subModel} ${model}` : `${brand} ${model}`;
  };

  const renderPriceInfo = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2">
          <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
          <span className="text-slate-400">Loading price data...</span>
        </div>
      );
    }

    return (
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
        <span className="text-2xl sm:text-3xl font-bold text-white">
          $
          {currentPrice?.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
        {priceChange !== null && percentChange !== null && (
          <div
            className={`flex items-center gap-1 ${
              isPositive ? 'text-emerald-400' : 'text-rose-400'
            }`}
          >
            {isPositive ? (
              <ArrowUpCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            ) : (
              <ArrowDownCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            )}
            <span className="text-sm sm:text-base font-medium">
              $
              {Math.abs(priceChange).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
            <span className="text-sm sm:text-base">({percentChange}%)</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative w-full min-h-[90vh] bg-slate-950">
      <GlowEffectContainer>
        <div className="flex flex-col items-center justify-center px-4 py-8 sm:py-12 md:py-16">
          <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-slate-950 to-slate-950" />
          <div className="absolute inset-0">
            <div className="absolute top-0 -right-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full blur-lg" />
            <div className="absolute bottom-0 -left-1/4 w-1/2 h-1/2 bg-blue-500/10 rounded-full blur-lg" />
          </div>

          <div className="relative z-10 w-full max-w-[1200px] mx-auto">
            <div className="space-y-6 sm:space-y-8 text-center">
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white tracking-tight mt-5">
                Track Watch Prices
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 text-transparent bg-clip-text">
                  Like Never Before
                </span>
              </h1>
              <p className="text-base sm:text-lg md:text-xl text-slate-300 max-w-2xl mx-auto font-light">
                Real-time market data from every major marketplace, helping you
                make informed decisions
              </p>

              <div className="flex justify-center mt-6 sm:mt-8">
                <GlowButton
                  className="w-full max-w-xs sm:w-auto px-6 sm:px-8 py-3 text-sm sm:text-base"
                  onClick={onTryItFree}
                />
              </div>
            </div>

            <div className="mt-8 sm:mt-12 md:mt-16">
              <div className="chart-container relative bg-slate-900/50 rounded-2xl p-4 sm:p-6 md:p-8 border border-slate-800/50">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/0 via-blue-500/10 to-blue-500/0 opacity-70 pointer-events-none blur-sm" />

                <div className="relative">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 mb-8">
                    <div className="lg:col-span-1">
                      <div className="flex items-start gap-4">
                        <div className="relative w-12 sm:w-16 h-12 sm:h-16 flex-shrink-0 mt-1">
                          <div className="absolute inset-0 bg-blue-500/20 rounded-full blur-md -z-10" />
                          <RolexImage
                            modelNumber={selectedSymbol.split(':')[1]}
                            size={64}
                            className="rounded-full shadow-lg relative z-10"
                          />
                        </div>
                        <div className="min-w-0 flex-grow">
                          <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 break-words">
                            {getFormattedWatchName()}
                          </h3>
                          {renderPriceInfo()}
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-1 lg:pt-2">
                      <HeroSearch
                        onSymbolSelect={handleSymbolSelect}
                        datafeed={memoizedDatafeed}
                        skipInitialLoad={searchInitializedRef.current}
                      />
                    </div>
                  </div>

                  <div className="h-[300px] sm:h-[400px] relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-500/5 to-transparent rounded-xl" />
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={priceData}
                        margin={
                          isMobile
                            ? { left: 0, right: 0, top: 10, bottom: 0 }
                            : { left: 20, right: 20 }
                        }
                      >
                        <defs>
                          <linearGradient
                            id="colorPrice"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor="#3b82f6"
                              stopOpacity={0.3}
                            />
                            <stop
                              offset="95%"
                              stopColor="#3b82f6"
                              stopOpacity={0}
                            />
                          </linearGradient>
                        </defs>
                        <XAxis
                          dataKey="date"
                          stroke="#64748b"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fill: '#94a3b8' }}
                          interval="preserveStartEnd"
                          minTickGap={30}
                          ticks={[
                            priceData[0]?.date || '',
                            isMobile
                              ? ''
                              : priceData[Math.floor(priceData.length / 2)]
                                  ?.date || '',
                            priceData[priceData.length - 1]?.date || '',
                          ]}
                          padding={
                            isMobile
                              ? { left: 0, right: 0 }
                              : { left: 30, right: 30 }
                          }
                          fontSize={14}
                          dy={isMobile ? 10 : 0}
                        />

                        <YAxis
                          stroke="#64748b"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fill: '#94a3b8' }}
                          tickFormatter={(value) =>
                            `$${(value / 1000).toFixed(1)}k`
                          }
                          width={isMobile ? 55 : 60}
                          fontSize={14}
                          dx={isMobile ? -8 : 0}
                        />
                        <CartesianGrid
                          stroke="#2d374850"
                          strokeDasharray="3 3"
                          vertical={false}
                        />
                        <Line
                          type="monotone"
                          dataKey="price"
                          stroke="#3b82f6"
                          strokeWidth={2}
                          dot={false}
                          activeDot={{
                            r: isMobile ? 6 : 8,
                            fill: '#3b82f6',
                            strokeWidth: 0,
                          }}
                          fillOpacity={1}
                          fill="url(#colorPrice)"
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1e293b',
                            border: 'none',
                            borderRadius: '0.75rem',
                            boxShadow: '0 0 20px rgba(59, 130, 246, 0.1)',
                            color: '#f8fafc',
                            padding: isMobile ? '12px' : '12px',
                            fontSize: isMobile ? '14px' : '14px',
                          }}
                          formatter={(value: number) => [
                            <span
                              key="tooltip"
                              style={{
                                display: 'flex',
                                gap: '4px',
                                fontSize: '14px',
                                color: '#3b82f6',
                              }}
                            >
                              $
                              {value.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </span>,
                            '',
                          ]}
                          labelFormatter={(label) => (
                            <span
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: '4px',
                              }}
                            >
                              <span style={{ color: '#ffffff' }}>{label}</span>
                            </span>
                          )}
                          separator=""
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </GlowEffectContainer>

      <style>
        {`
  .chart-container {
    box-shadow: 0 0 30px 1px rgba(59, 130, 246, 0.1);
  }
  `}
      </style>
    </div>
  );
};

export default PriceTrackingHero;
